import { lang } from "./lang";
import { MsalToken, RequestObject, UploadQueueItem } from "./interface";
import { store } from "./store";
import { silentRenew, updateLastPage } from "./actions/adLogin";
import { alertBox } from "./actions/alertBox";
import { clearSession } from "./actions/clearSession";
import * as HelperSrc from "./HelperSrc";
import * as ModelHelperSrc from "./model/HelperSrc";
import * as Instance from "./Instance";

const debouncePeriod = 1000;

type HttpError = {
    response?: { status?: number };
    status?: number;
    message?: string;
    code?: string;
};

function isHttpError(error: unknown): error is HttpError {
    return typeof error === "object" && error !== null;
}

/**
 * Simple requests to our API that handles authentication and default
 * behaviour (error popups, redirecting to login screen).
 *
 * @param d Configuration and data for the request
 */
async function request<T, R>(reqObject: RequestObject<R>): Promise<ModelHelperSrc.Iresponse<T> | T | null> {
    let auth: string | null = null;

    if (!reqObject.noAuth) {
        if (reqObject.username && reqObject.psw) {
            auth = "Basic " + btoa(reqObject.username + ":" + reqObject.psw);
        } else {
            const tkn: string = "";

            try {
                const r = JSON.parse(tkn) as MsalToken;

                if (r.accessToken) {
                    let tokn: MsalToken | null = null;

                    if (Date.now() - (r.lastUpdate || 0) > 60 * 60 * 1000 - 60000) {
                        tokn = await silentRenew(r);
                        tokn = tokn || r;
                    }

                    const tokenJson = tokn || r;
                    auth = "Bearer " + tokenJson?.accessToken;

                    if (tokn) {
                        localStorage.setItem(
                            `${HelperSrc.LABEL}_token`,
                            JSON.stringify({
                                accessToken: tokenJson?.accessToken,
                                homeAccountId: tokenJson?.homeAccountId,
                                lastUpdate: Date.now()
                            })
                        );
                    }
                }
            } catch (e) {
                if (e instanceof Error) {
                    HelperSrc.writeLog("request.tsx -> request()", e.message);
                }

                auth = tkn && tkn.length > 6 ? "Token " + tkn : null;
            }
        }
    }

    let body: string | FormData;
    const headers = {} as Record<string, string>;

    if (reqObject.sendtype === "form" || (!reqObject.sendtype && hasFile(reqObject.data as UploadQueueItem | UploadQueueItem[]))) {
        body = new FormData();

        for (const el in reqObject.data) {
            body.append(el, reqObject.data[el] as Blob);
        }
    } else {
        body = JSON.stringify(reqObject.data);
        headers["Content-Type"] = "application/json";
    }
    // Makes all paths absolute
    const path = `${reqObject.path || ""}`;

    if (auth) {
        headers.Authorization = auth;
    }

    const dString = JSON.stringify({
        ...reqObject,
        ...(reqObject.data
            ? {
                  data: Object.entries(reqObject.data as { [key: string]: string | File }).reduce(
                      (c, n) => {
                          const key = n[0];
                          const val = n[1];
                          return {
                              ...c,
                              // if d.data came from a form (which can contain File objects) flatten it as
                              // [key: string]: val: string using the filename stored in File.name
                              [key]: val instanceof File ? val.name : val
                          };
                      },
                      {} as { [key: string]: string }
                  )
              }
            : {})
    });

    const now = Date.now();
    const requestBuffer = {
        time: 0,
        request: "",
        promise: undefined as undefined | Promise<ModelHelperSrc.Iresponse<T> | T>
    };

    // only create a new fetch() when:
    if (
        // it's a different request, or
        dString !== requestBuffer.request ||
        // enough time has elapsed
        now - requestBuffer.time > debouncePeriod
    ) {
        // make new fetch
        const method = (reqObject.method || "POST").toUpperCase();

        let newFetch: ModelHelperSrc.Iresponse<T> | T;

        if (method === "GET") {
            newFetch = await Instance.api.get<ModelHelperSrc.Iresponse<T> | T>(path, { headers }).then((response) => response.data);
        } else if (method === "PUT") {
            newFetch = await Instance.api.put<ModelHelperSrc.Iresponse<T> | T>(path, { headers }, body).then((response) => response.data);
        } else if (method === "PATCH") {
            newFetch = await Instance.api.patch<ModelHelperSrc.Iresponse<T> | T>(path, { headers }, body).then((response) => response.data);
        } else if (method === "DELETE") {
            newFetch = await Instance.api.delete<ModelHelperSrc.Iresponse<T> | T>(path, { headers }, body).then((response) => response.data);
        } else {
            newFetch = await Instance.api.post<ModelHelperSrc.Iresponse<T> | T>(path, { headers }, body).then((response) => response.data);
        }

        requestBuffer.request = dString;
        requestBuffer.promise = Promise.resolve(newFetch);
    }

    requestBuffer.time = now;
    // otherwise, just return the existing fetch promise;
    const fetchPromise = requestBuffer.promise;

    const onError = reqObject.onError;

    try {
        const resp = await fetchPromise;
        return resp || null;
    } catch (error: unknown) {
        if (isHttpError(error)) {
            const resp = error.response;
            if (resp?.status === 404) {
                if (onError) onError(resp, "Endpoint not found...");
                return null;
            } else if (resp?.status === 405) {
                if (onError) onError(resp, "Wrong method used (GET, POST, PATCH etc)");
                return null;
            } else if (resp?.status === 401 || resp?.status === 403) {
                clearSession();
                if (!location.pathname.endsWith("/login")) {
                    store.doNotice(lang.unauthorizedError);
                    updateLastPage();
                }
                return null;
            } else if (resp?.status === 400 || resp?.status === 429) {
                const msg = error.code || error?.message || "Unknown error";
                if (onError) onError(resp, `Bad Response - Code  ${resp.status} - ${msg}`);
                if (error?.message) {
                    alertBox(msg).catch(() => {
                        if (onError) onError(resp, "An unexpected error occurred");
                    });
                }
                return null;
            } else {
                const txt = error.message || "Unknown error";
                if (onError) onError(resp, `Unknown Error - Code  ${error.status || ""} - ${txt}`);
                return null;
            }
        } else {
            if (onError) onError(null, "An unexpected error occurred");
            return null;
        }
    }
}

function hasFile(data: UploadQueueItem | UploadQueueItem[]) {
    if (!data) {
        return false;
    }
    for (const d in data) {
        if (data[d] instanceof File) {
            return true;
        }
    }
    return false;
}

export { request };
